
import React, { useState, useEffect, useCallback, useRef } from "react"
import { MapContainer, TileLayer, Marker, useMapEvents, Circle } from "react-leaflet"
import { Button, Form, Row, Col, Alert, InputGroup, ListGroup } from "react-bootstrap"
import L from "leaflet"
import "leaflet/dist/leaflet.css"

// Fix for default markers in react-leaflet
delete L.Icon.Default.prototype._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
})

/**
 * @typedef {object} LocationData
 * @property {string} latitude
 * @property {string} longitude
 * @property {string} [address]
 * @property {string} [city]
 * @property {string} [state]
 * @property {string} [country]
 * @property {string} [zipCode]
 * @property {string} [displayName]
 * @property {string} [placeType]
 */

/**
 * @typedef {object} DeliveryZone
 * @property {number} from_miles
 * @property {number} to_miles
 * @property {string} [color]
 */

/**
 * @typedef {object} SearchSuggestionAddress
 * @property {string} [house_number]
 * @property {string} [road]
 * @property {string} [neighbourhood]
 * @property {string} [suburb]
 * @property {string} [city]
 * @property {string} [state]
 * @property {string} [postcode]
 * @property {string} [country]
 * @property {string} [building]
 * @property {string} [shop]
 * @property {string} [amenity]
 */

/**
 * @typedef {object} SearchSuggestion
 * @property {string} place_id
 * @property {string} display_name
 * @property {string} lat
 * @property {string} lon
 * @property {string} type
 * @property {number} importance
 * @property {SearchSuggestionAddress} address
 */

/**
 * @typedef {object} LocationSelectorMapProps
 * @property {string} [initialLatitude]
 * @property {string} [initialLongitude]
 * @property {string} [initialAddress]
 * @property {(locationData: LocationData) => void} onLocationChange
 * @property {(address: string) => void} [onAddressChange]
 * @property {boolean} [showDeliveryZones]
 * @property {DeliveryZone[]} [deliveryZones]
 * @property {boolean} [showAddressSearch]
 * @property {boolean} [showCurrentLocation]
 * @property {boolean} [showCoordinateInputs]
 * @property {string} [mapHeight]
 * @property {number} [defaultZoom]
 * @property {[number, number]} [defaultCenter]
 * @property {string} [className]
 * @property {boolean} [disabled]
 * @property {boolean} [useGoogleMaps]
 * @property {string} [googleMapsApiKey]
 */

/**
 * A comprehensive location selector component using Leaflet maps with optional Google Maps API integration.
 * @param {LocationSelectorMapProps} props
 */
const LocationSelectorMap = ({
  initialLatitude = "",
  initialLongitude = "",
  initialAddress = "",
  onLocationChange,
  onAddressChange,
  showDeliveryZones = false,
  deliveryZones = [],
  showAddressSearch = true,
  showCurrentLocation = true,
  showCoordinateInputs = true,
  mapHeight = "400px",
  defaultZoom = 13,
  defaultCenter = [40.7128, -74.006], // NYC default
  className = "",
  disabled = false,
  useGoogleMaps = false,
  googleMapsApiKey = "",
}) => {
  // State management
  const [mapCenter, setMapCenter] = useState(defaultCenter)
  const [selectedPosition, setSelectedPosition] = useState(null)
  const [searchAddress, setSearchAddress] = useState(initialAddress)
  const [coordinates, setCoordinates] = useState({
    latitude: initialLatitude,
    longitude: initialLongitude,
  })

  // Autocomplete states
  const [suggestions, setSuggestions] = useState([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [isSearching, setIsSearching] = useState(false)
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1)

  // Loading and error states
  const [isGeocoding, setIsGeocoding] = useState(false)
  const [isGettingLocation, setIsGettingLocation] = useState(false)
  const [error, setError] = useState("")
  const [success, setSuccess] = useState("")

  // Refs
  const searchInputRef = useRef(null)
  const debounceRef = useRef()

  // Map click handler component
  const LocationMarker = () => {
    useMapEvents({
      click(e) {
        if (disabled) return

        const { lat, lng } = e.latlng
        const newPosition = [lat, lng]

        setSelectedPosition(newPosition)
        setCoordinates({
          latitude: lat.toFixed(8),
          longitude: lng.toFixed(8),
        })

        // Reverse geocode to get address
        reverseGeocode(lat, lng)
        setError("")
      },
    })

    return selectedPosition === null ? null : <Marker position={selectedPosition} />
  }

  // Enhanced reverse geocoding with better building/POI detection
  const reverseGeocode = async (lat, lng) => {
    try {
      if (useGoogleMaps && googleMapsApiKey) {
        await reverseGeocodeGoogle(lat, lng)
      } else {
        await reverseGeocodeNominatim(lat, lng)
      }
    } catch (error) {
      console.log("Reverse geocoding failed:", error)
      // Still call onLocationChange with coordinates only
      onLocationChange({
        latitude: lat.toFixed(8),
        longitude: lng.toFixed(8),
      })
    }
  }

  // Google Maps reverse geocoding
  const reverseGeocodeGoogle = async (lat, lng) => {
    const response = await fetch(
      `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${googleMapsApiKey}`
    )
    const data = await response.json()

    if (data.results && data.results.length > 0) {
      const result = data.results[0]
      const locationData = {
        latitude: lat.toFixed(8),
        longitude: lng.toFixed(8),
        address: result.formatted_address,
        displayName: result.formatted_address,
      }

      // Extract address components
      result.address_components?.forEach(component => {
        if (component.types.includes("locality")) {
          locationData.city = component.long_name
        } else if (component.types.includes("administrative_area_level_1")) {
          locationData.state = component.short_name
        } else if (component.types.includes("country")) {
          locationData.country = component.long_name
        } else if (component.types.includes("postal_code")) {
          locationData.zipCode = component.long_name
        }
      })

      setSearchAddress(result.formatted_address)
      onLocationChange(locationData)
      onAddressChange?.(result.formatted_address)
    }
  }

  // Enhanced Nominatim reverse geocoding
  const reverseGeocodeNominatim = async (lat, lng) => {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1&extratags=1&namedetails=1&zoom=18`
    )
    const data = await response.json()

    if (data && data.display_name) {
      const locationData = {
        latitude: lat.toFixed(8),
        longitude: lng.toFixed(8),
        address: data.display_name,
        displayName: data.display_name,
        city: data.address?.city || data.address?.town || data.address?.village || "",
        state: data.address?.state || "",
        country: data.address?.country || "",
        zipCode: data.address?.postcode || "",
        placeType: data.type || data.class || "",
      }

      setSearchAddress(data.display_name)
      onLocationChange(locationData)
      onAddressChange?.(data.display_name)
    }
  }

  // Enhanced search with autocomplete
  const searchWithAutocomplete = async query => {
    if (!query.trim() || query.length < 3) {
      setSuggestions([])
      setShowSuggestions(false)
      return
    }

    setIsSearching(true)

    try {
      if (useGoogleMaps && googleMapsApiKey) {
        await searchGooglePlaces(query)
      } else {
        await searchNominatim(query)
      }
    } catch (error) {
      console.log("Search failed:", error)
      setSuggestions([])
    } finally {
      setIsSearching(false)
    }
  }

  // Google Places autocomplete
  const searchGooglePlaces = async query => {
    const response = await fetch(
      `https://maps.googleapis.com/maps/api/place/autocomplete/json?input=${encodeURIComponent(
        query
      )}&key=${googleMapsApiKey}`
    )
    const data = await response.json()

    if (data.predictions) {
      const googleSuggestions = data.predictions.map(prediction => ({
        place_id: prediction.place_id,
        display_name: prediction.description,
        lat: "",
        lon: "",
        type: prediction.types[0] || "address",
        importance: 1,
        address: {},
      }))

      setSuggestions(googleSuggestions)
      setShowSuggestions(true)
    }
  }

  // Enhanced Nominatim search with better parameters
  const searchNominatim = async query => {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
        query
      )}&limit=8&addressdetails=1&extratags=1&namedetails=1&dedupe=1&bounded=0`
    )
    const data = await response.json()

    if (data && data.length > 0) {
      const nominatimSuggestions = data.map(item => ({
        place_id: item.place_id,
        display_name: item.display_name,
        lat: item.lat,
        lon: item.lon,
        type: item.type || item.class || "address",
        importance: item.importance || 0,
        address: item.address || {},
      }))

      // Sort by importance and type priority
      nominatimSuggestions.sort((a, b) => {
        const typeOrder = ["building", "shop", "amenity", "house", "address"]
        const aTypeIndex = typeOrder.indexOf(a.type)
        const bTypeIndex = typeOrder.indexOf(b.type)

        if (aTypeIndex !== -1 && bTypeIndex !== -1) {
          return aTypeIndex - bTypeIndex
        }
        return b.importance - a.importance
      })

      setSuggestions(nominatimSuggestions)
      setShowSuggestions(true)
    }
  }

  // Handle suggestion selection
  const selectSuggestion = async suggestion => {
    setSearchAddress(suggestion.display_name)
    setShowSuggestions(false)
    setSuggestions([])
    setSelectedSuggestionIndex(-1)

    if (useGoogleMaps && googleMapsApiKey && !suggestion.lat) {
      // Get details for Google Places
      await getGooglePlaceDetails(suggestion.place_id)
    } else {
      // Use Nominatim data
      const lat = Number.parseFloat(suggestion.lat)
      const lng = Number.parseFloat(suggestion.lon)

      setSelectedPosition([lat, lng])
      setMapCenter([lat, lng])
      setCoordinates({
        latitude: lat.toFixed(8),
        longitude: lng.toFixed(8),
      })

      const locationData = {
        latitude: lat.toFixed(8),
        longitude: lng.toFixed(8),
        address: suggestion.display_name,
        displayName: suggestion.display_name,
        city: suggestion.address?.city || suggestion.address?.town || suggestion.address?.village || "",
        state: suggestion.address?.state || "",
        country: suggestion.address?.country || "",
        zipCode: suggestion.address?.postcode || "",
        placeType: suggestion.type,
      }

      onLocationChange(locationData)
      setSuccess("Location selected successfully!")
      setTimeout(() => setSuccess(""), 3000)
    }
  }

  // Get Google Place details
  const getGooglePlaceDetails = async placeId => {
    const response = await fetch(
      `https://maps.googleapis.com/maps/api/place/details/json?place_id=${placeId}&key=${googleMapsApiKey}`
    )
    const data = await response.json()

    if (data.result && data.result.geometry) {
      const { lat, lng } = data.result.geometry.location
      setSelectedPosition([lat, lng])
      setMapCenter([lat, lng])
      setCoordinates({
        latitude: lat.toFixed(8),
        longitude: lng.toFixed(8),
      })

      const locationData = {
        latitude: lat.toFixed(8),
        longitude: lng.toFixed(8),
        address: data.result.formatted_address,
        displayName: data.result.name || data.result.formatted_address,
      }

      onLocationChange(locationData)
      setSuccess("Location selected successfully!")
      setTimeout(() => setSuccess(""), 3000)
    }
  }

  // Debounced search
  const handleSearchChange = value => {
    setSearchAddress(value)
    onAddressChange?.(value)

    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }

    debounceRef.current = setTimeout(() => {
      searchWithAutocomplete(value)
    }, 300)
  }

  // Handle keyboard navigation
  const handleKeyDown = e => {
    if (!showSuggestions || suggestions.length === 0) return

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault()
        setSelectedSuggestionIndex(prev => (prev < suggestions.length - 1 ? prev + 1 : prev))
        break
      case "ArrowUp":
        e.preventDefault()
        setSelectedSuggestionIndex(prev => (prev > 0 ? prev - 1 : prev))
        break
      case "Enter":
        e.preventDefault()
        if (selectedSuggestionIndex >= 0) {
          selectSuggestion(suggestions[selectedSuggestionIndex])
        }
        break
      case "Escape":
        setShowSuggestions(false)
        setSelectedSuggestionIndex(-1)
        break
    }
  }

  // Get user's current location
  const getCurrentLocation = () => {
    if (!navigator.geolocation || disabled) {
      setError("Geolocation is not supported by this browser.")
      return
    }

    setIsGettingLocation(true)
    setError("")

    navigator.geolocation.getCurrentPosition(
      position => {
        const { latitude: lat, longitude: lng } = position.coords
        const newPosition = [lat, lng]

        setSelectedPosition(newPosition)
        setMapCenter(newPosition)
        setCoordinates({
          latitude: lat.toFixed(8),
          longitude: lng.toFixed(8),
        })

        reverseGeocode(lat, lng)
        setSuccess("Current location detected successfully!")
        setTimeout(() => setSuccess(""), 3000)
        setIsGettingLocation(false)
      },
      error => {
        let errorMessage = "Failed to get current location."
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = "Location access denied by user."
            break
          case error.POSITION_UNAVAILABLE:
            errorMessage = "Location information is unavailable."
            break
          case error.TIMEOUT:
            errorMessage = "Location request timed out."
            break
        }
        setError(errorMessage)
        setIsGettingLocation(false)
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000,
      }
    )
  }

  // Handle manual coordinate input
  const handleCoordinateChange = useCallback(
    (field, value) => {
      if (disabled) return

      setCoordinates(prev => ({ ...prev, [field]: value }))

      const lat = field === "latitude" ? Number.parseFloat(value) : Number.parseFloat(coordinates.latitude)
      const lng = field === "longitude" ? Number.parseFloat(value) : Number.parseFloat(coordinates.longitude)

      if (!isNaN(lat) && !isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
        const newPosition = [lat, lng]
        setSelectedPosition(newPosition)
        setMapCenter(newPosition)

        onLocationChange({
          latitude: lat.toFixed(8),
          longitude: lng.toFixed(8),
        })

        reverseGeocode(lat, lng)
      }
    },
    [coordinates.latitude, coordinates.longitude, disabled, onLocationChange]
  )

  const milesToMeters = miles => miles * 1609.34
  const getZoneColor = index => `hsl(${index * 60}, 70%, 50%)`

  const formatSuggestionDisplay = suggestion => {
    const parts = suggestion.display_name.split(",")
    const mainPart = parts[0]
    const secondaryPart = parts.slice(1, 3).join(",")

    return (
      <div>
        <div className="fw-bold">{mainPart}</div>
        <small className="text-muted">{secondaryPart}</small>
        <small className="badge bg-secondary ms-2">{suggestion.type}</small>
      </div>
    )
  }

  // Set initial position if coordinates are provided
  useEffect(() => {
    if (initialLatitude && initialLongitude) {
      const lat = Number.parseFloat(initialLatitude)
      const lng = Number.parseFloat(initialLongitude)
      if (!isNaN(lat) && !isNaN(lng)) {
        setSelectedPosition([lat, lng])
        setMapCenter([lat, lng])
        setCoordinates({
          latitude: initialLatitude,
          longitude: initialLongitude,
        })
      }
    }
  }, [initialLatitude, initialLongitude])

  // Update search address when initial address changes
  useEffect(() => {
    setSearchAddress(initialAddress)
  }, [initialAddress])

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
    }
  }, [])

  return (
    <div className={`location-selector-map ${className}`}>
      {/* Search and Controls */}
      {(showAddressSearch || showCurrentLocation) && (
        <Row className="mb-3">
          {showAddressSearch && (
            <Col md={showCurrentLocation ? 8 : 12}>
              <Form.Group>
                <Form.Label>Search Location</Form.Label>
                <div className="position-relative">
                  <InputGroup>
                    <Form.Control
                      ref={searchInputRef}
                      type="text"
                      placeholder="Enter address, building name, or landmark"
                      value={searchAddress}
                      onChange={e => handleSearchChange(e.target.value)}
                      onKeyDown={handleKeyDown}
                      onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                      disabled={disabled}
                    />
                    <Button variant="outline-primary" disabled={isSearching || disabled}>
                      {isSearching ? "🔄" : "🔍"}
                    </Button>
                  </InputGroup>

                  {/* Autocomplete Suggestions */}
                  {showSuggestions && suggestions.length > 0 && (
                    <div
                      className="position-absolute w-100 bg-white border rounded shadow-lg"
                      style={{ zIndex: 1000, maxHeight: "300px", overflowY: "auto" }}
                    >
                      <ListGroup variant="flush">
                        {suggestions.map((suggestion, index) => (
                          <ListGroup.Item
                            key={suggestion.place_id}
                            action
                            active={index === selectedSuggestionIndex}
                            onClick={() => selectSuggestion(suggestion)}
                            className="cursor-pointer"
                          >
                            {formatSuggestionDisplay(suggestion)}
                          </ListGroup.Item>
                        ))}
                      </ListGroup>
                    </div>
                  )}
                </div>
              </Form.Group>
            </Col>
          )}

          {showCurrentLocation && (
            <Col md={showAddressSearch ? 4 : 12} className="d-flex align-items-end">
              <Button
                variant="outline-success"
                onClick={getCurrentLocation}
                disabled={isGettingLocation || disabled}
                className="mb-3 w-100"
              >
                {isGettingLocation ? "Getting Location..." : "📍 Current Location"}
              </Button>
            </Col>
          )}
        </Row>
      )}

      {/* Error and Success Messages */}
      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}
      {success && (
        <Alert variant="success" className="mb-3">
          {success}
        </Alert>
      )}

      {/* Map Container */}
      <div style={{ height: mapHeight, width: "100%" }} className="border rounded mb-3">
        <MapContainer
          center={mapCenter}
          zoom={defaultZoom}
          style={{ height: "100%", width: "100%" }}
          key={`${mapCenter[0]}-${mapCenter[1]}`} // Force re-render when center changes
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <LocationMarker />

          {/* Show delivery zones if enabled and position is selected */}
          {showDeliveryZones &&
            selectedPosition &&
            deliveryZones.map((zone, index) => (
              <Circle
                key={index}
                center={selectedPosition}
                radius={milesToMeters(zone.to_miles)}
                pathOptions={{
                  color: zone.color || getZoneColor(index),
                  fillColor: zone.color || getZoneColor(index),
                  fillOpacity: 0.1,
                  weight: 2,
                }}
              />
            ))}
        </MapContainer>
      </div>

      {/* Coordinate Inputs */}
      {showCoordinateInputs && (
        <Row className="mb-3">
          <Col md={6}>
            <Form.Group>
              <Form.Label>Latitude</Form.Label>
              <Form.Control
                type="number"
                step="0.00000001"
                value={coordinates.latitude}
                onChange={e => handleCoordinateChange("latitude", e.target.value)}
                placeholder="Enter latitude or select on map"
                disabled={disabled}
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Longitude</Form.Label>
              <Form.Control
                type="number"
                step="0.00000001"
                value={coordinates.longitude}
                onChange={e => handleCoordinateChange("longitude", e.target.value)}
                placeholder="Enter longitude or select on map"
                disabled={disabled}
              />
            </Form.Group>
          </Col>
        </Row>
      )}

      {/* Instructions and Status */}
      <div className="map-instructions">
        <small className="text-muted">
          <strong>Instructions:</strong>
          <ul className="mb-0 mt-1">
            <li>Type at least 3 characters to see address suggestions</li>
            <li>Use arrow keys to navigate suggestions, Enter to select</li>
            <li>Click anywhere on the map to select a location</li>
            {showCurrentLocation && <li>Click "Current Location" to automatically detect your location</li>}
            {showDeliveryZones && <li>Colored circles show delivery coverage areas</li>}
            {showCoordinateInputs && <li>Manually enter coordinates in the input fields</li>}
          </ul>
        </small>
      </div>

      {/* Selected Location Display */}
      {selectedPosition && (
        <Alert variant="info" className="mt-3">
          <strong>Selected Location:</strong>
          <br />
          <small>
            Latitude: {selectedPosition[0].toFixed(8)} | Longitude: {selectedPosition[1].toFixed(8)}
            {searchAddress && (
              <>
                <br />
                Address: {searchAddress}
              </>
            )}
          </small>
        </Alert>
      )}
    </div>
  )
}

export default LocationSelectorMap